import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, Download } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";

/**
 * TermsPage — full-screen view of the latest Terms & Conditions.
 *
 * Source of truth is `/public/terms.md` (markdown). We fetch it at runtime
 * and render it as a styled article. This way, legal can update the policy
 * by editing the markdown file without touching React code.
 *
 * The same /terms route is linked from:
 *   • TermsGate first-launch acceptance dialog (Read full terms)
 *   • LoginPage footer
 *   • Header dropdown ("Terms & Conditions")
 */
export default function TermsPage() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch("/terms.md", { cache: "no-store" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error("Failed to load"))))
      .then((txt) => { if (alive) { setContent(txt); setLoading(false); } })
      .catch(() => { if (alive) { setContent(""); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 font-body mb-3"
          data-testid="terms-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>

        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-7 h-7 text-gray-900 shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">
                Terms &amp; Conditions
              </h1>
              <p className="text-sm text-gray-500 font-body">
                Please read carefully. Continued use of Guard means you accept these terms.
              </p>
            </div>
          </div>
          <a
            href="/terms.md"
            download="Guard_Terms_and_Conditions.md"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 hover:text-black border border-gray-300 hover:bg-gray-100 rounded-sm px-3 py-1.5 shrink-0"
            data-testid="terms-download"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </a>
        </div>

        <article
          className="bg-white border border-gray-200 rounded-sm p-5 sm:p-8 shadow-sm font-body"
          data-testid="terms-content"
        >
          {loading ? (
            <div className="text-center py-16 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading terms…
            </div>
          ) : content ? (
            <div className="prose prose-sm sm:prose-base max-w-none prose-headings:font-heading prose-headings:tracking-tight prose-h1:text-3xl prose-h1:font-black prose-h2:text-xl prose-h2:font-extrabold prose-h2:mt-8 prose-h2:pb-1 prose-h2:border-b prose-h2:border-gray-200 prose-h3:text-base prose-h3:font-bold prose-strong:text-gray-900 prose-em:text-gray-600 prose-a:text-red-700 prose-a:no-underline hover:prose-a:underline prose-table:text-sm prose-th:bg-gray-50 prose-li:my-1">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-sm text-red-600 font-body mb-3">
                Couldn&apos;t load the terms file. Please email{" "}
                <a className="underline" href="mailto:alerts@contritrack.com">
                  alerts@contritrack.com
                </a>
                .
              </p>
              <Button asChild variant="outline" size="sm">
                <a href="/terms.md">Open raw file</a>
              </Button>
            </div>
          )}
        </article>

        <p className="text-[11px] text-gray-400 font-body text-center mt-6" data-testid="terms-footer">
          Effective February 03, 2026 · &copy; Contrivances Engineering Ent. Ltd.
        </p>
      </div>
    </div>
  );
}
